import { useCallback, useMemo, useState } from "react";
import { useTaskCompletion } from "@/modules/tasks/hooks/use-task-completion.hook";
import { TaskStatusesBackend } from "@/modules/tasks/enums";

import { TasksResponse } from "@/modules/tasks/dtos";
import { useGetTrackTasks } from "./get-track-tasks";

const useTrackTasks = ({ trackUId }: { trackUId?: string }) => {
  const { trackTasks, isLoading, refetch } = useGetTrackTasks({ trackUId });

  const [selectedTask, setSelectedTask] = useState<TasksResponse | null>(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const {
    handleMarkComplete,
    completingTaskId,
    isTaskOptimisticallyCompleted,
  } = useTaskCompletion({
    queryKeys: [
      "track-tasks",
      "tasks-dashboard",
      "task-statistics",
      "tasks-count",
    ],
    successMessage: "Task completed successfully",
    onSuccess: refetch,
    refetch,
    enableOptimisticUpdates: true,
  });

  const tasks = useMemo(
    () =>
      trackTasks?.map((task) => ({
        ...task,
        status: isTaskOptimisticallyCompleted(task.uid)
          ? TaskStatusesBackend.completed
          : task.status,
      })) ?? [],
    [trackTasks, isTaskOptimisticallyCompleted],
  );

  const openTask = useCallback((task: TasksResponse) => {
    setSelectedTask(task);
    setIsSidebarOpen(true);
  }, []);

  const closeSidebar = useCallback(() => {
    setSelectedTask(null);
    setIsSidebarOpen(false);
  }, []);

  return {
    isLoading,
    tasks,
    completingTaskId,
    selectedTask,
    isSidebarOpen,
    openTask,
    closeSidebar,
    handleMarkComplete,
  };
};

export default useTrackTasks;
